import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";
import { motion } from "framer-motion";
import { AlertCircle, Loader } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import BillingService from "@/lib/billingService";
import PayPalUserPaymentOption from "@/components/billing/PayPalUserPaymentOption";
import PricingComparisonSection from "@/components/billing/PricingComparisonSection";
import ReceiptDownloadButton from "@/components/billing/ReceiptDownloadButton";
import { useLanguage } from "@/lib/LanguageContext";
import { getTranslationsForLanguage } from "@/i18n";
import WhySubscribeSection from "@/components/subscription/WhySubscribeSection";
import SimpleSubscriptionTiers from "@/components/subscription/SimpleSubscriptionTiers";
import { getSubscriptionPitchCopy, localizeBillingValue } from "@/lib/subscriptionPitchTranslations";

export default function Billing() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const { language } = useLanguage();
  const t = getTranslationsForLanguage(language, "billing");
  const planCopy = getTranslationsForLanguage(language, "subscriptionPlans");
  const fallbackPlanCopy = getTranslationsForLanguage("en", "subscriptionPlans");
  const premiumCopy = getSubscriptionPitchCopy(language);
  const paypalCopy = { ...t.paypal, openingPaypal: premiumCopy.billing.openingPaypal };
  const localizeSubscriptionPlan = (plan) => {
    if (!plan) return plan;
    const premiumTier = getSubscriptionPitchCopy(language).tiers[1];
    const localized = planCopy?.[plan.tier] || (plan.tier === "premium" ? { name: premiumTier.name, description: premiumTier.items.join(", ") } : null) || fallbackPlanCopy?.[plan.tier];
    return localized ? { ...plan, name: localized.name || plan.name, description: localized.description || plan.description } : plan;
  };
  const formatText = (template, values = {}) => Object.entries(values).reduce((text, [key, value]) => text.replace(`{${key}}`, value), template);
  const formatDate = (date) => new Date(date).toLocaleDateString(language || "en");

  useEffect(() => {
    base44.auth.me()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setAuthChecked(true));
  }, []);

  // Fetch user subscription
  const { data: subscriptionData } = useQuery({
    queryKey: ["user-subscription", user?.email],
    queryFn: () => BillingService.getUserSubscription(user?.email),
    enabled: !!user?.email
  });

  // Fetch plans
  const { data: plans = [] } = useQuery({
    queryKey: ["subscription-plans"],
    queryFn: () => BillingService.getPlans()
  });

  // Fetch transactions
  const { data: transactions = [] } = useQuery({
    queryKey: ["user-transactions", user?.email],
    queryFn: () => BillingService.getUserTransactions(user?.email, 20),
    enabled: !!user?.email
  });

  const subscription = subscriptionData || {
    tier: "basic",
    billing_cycle: "monthly",
    status: "inactive",
    current_tier_price: 0,
    payment_provider: "paypal"
  };
  const localizedPlans = useMemo(() => plans.map((plan) => localizeSubscriptionPlan(plan, language)), [plans, language]);
  const localizedCurrentPlan = localizeSubscriptionPlan({ tier: subscription.tier, name: subscription.tier }, language);

  // Fetch upgrade suggestion
  const { data: upgradePlan } = useQuery({
    queryKey: ["upgrade-plan", subscription?.tier],
    queryFn: () => BillingService.getUpgradeSuggestion(subscription?.tier),
    enabled: !!subscription?.tier
  });
  const localizedUpgradePlan = localizeSubscriptionPlan(upgradePlan, language);

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-background px-4 py-6 sm:p-6">
        <div className="mx-auto w-full max-w-7xl space-y-8">
          <WhySubscribeSection showNewsletter />
          <SimpleSubscriptionTiers />
          <Card className="mx-auto w-full max-w-md text-center">
            <CardHeader>
              <div className="mx-auto mb-3 grid h-14 w-14 place-items-center rounded-2xl bg-primary/10 text-primary">
                <AlertCircle className="h-7 w-7" />
              </div>
              <CardTitle>{t.signInTitle}</CardTitle>
              <CardDescription>{t.signInDescription}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => base44.auth.redirectToLogin(window.location.href)}>
                {t.signInButton}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background px-4 py-6 sm:p-6">
      <div className="mx-auto w-full max-w-7xl space-y-8">
        {/* Header */}
        <motion.div
          className="space-y-2"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h1 className="text-4xl font-bold font-heading">{t.title}</h1>
          <p className="text-muted-foreground">{t.subtitle}</p>
        </motion.div>

        <WhySubscribeSection />

        <SimpleSubscriptionTiers />

        {/* Current Plan Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <Card className="bg-gradient-to-r from-primary/10 to-accent/10 border-primary/20">
            <CardHeader>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <CardTitle>{t.currentPlan}</CardTitle>
                  <CardDescription>{t.currentPlanDescription}</CardDescription>
                </div>
                <Badge className="bg-primary text-white capitalize">{localizedCurrentPlan.name}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t.currentTier}</p>
                  <p className="text-lg font-bold capitalize">{localizedCurrentPlan.name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t.billingCycle}</p>
                  <p className="text-lg font-bold capitalize">{localizeBillingValue(subscription.billing_cycle, language, "cycles")}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t.status}</p>
                  <Badge variant={subscription.status === "active" ? "default" : "outline"}>
                    {localizeBillingValue(subscription.status, language, "statuses")}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">{t.renewsOn}</p>
                  <p className="text-lg font-bold">
                    {subscription.current_period_end 
                      ? formatDate(subscription.current_period_end)
                      : t.notAvailable
                    }
                  </p>
                </div>
              </div>

              {/* Upgrade Suggestion */}
              {localizedUpgradePlan && (
                <div className="flex flex-col gap-4 rounded-lg bg-white/50 p-4 dark:bg-slate-900/50 sm:flex-row sm:items-start sm:justify-between">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold mb-1">{t.readyForMore}</p>
                    <p className="text-xs text-muted-foreground">
                      {premiumCopy.billing.upgradeBlurb}
                    </p>
                  </div>
                  <Button className="gap-2">
                    {formatText(t.upgradeTo, { plan: localizedUpgradePlan.name })}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>

        <Tabs defaultValue="plans" className="space-y-6">
          <TabsList className="grid h-auto w-full grid-cols-1 gap-2 p-1 sm:grid-cols-3">
            <TabsTrigger value="plans">{t.plansTab}</TabsTrigger>
            <TabsTrigger value="billing">{t.historyTab}</TabsTrigger>
            <TabsTrigger value="payment">{t.paymentTab}</TabsTrigger>
          </TabsList>

          {/* Plans Tab */}
          <TabsContent value="plans" className="space-y-6">
            <PricingComparisonSection plans={localizedPlans} currentTier={subscription.tier} copy={t.pricing} paypalCopy={paypalCopy} />
          </TabsContent>

          {/* Billing History Tab */}
          <TabsContent value="billing" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t.billingHistory}</CardTitle>
                <CardDescription>{formatText(t.transactions, { count: transactions.length })}</CardDescription>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <p className="text-sm">{t.noHistory}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.map((tx, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: idx * 0.05 }}
                        className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted/50 transition-colors"
                      >
                        <div className="flex-1">
                          <p className="text-sm font-semibold capitalize">
                            {localizeBillingValue(tx.transaction_type || "payment", language, "transactionTypes")}
                          </p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {formatDate(tx.created_date)} • {localizeSubscriptionPlan({ tier: tx.plan_tier, name: tx.plan_tier }, language)?.name || tx.plan_tier}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold">${tx.amount}</p>
                          <Badge variant={tx.status === "succeeded" ? "default" : "outline"}>
                            {localizeBillingValue(tx.status, language, "statuses")}
                          </Badge>
                        </div>
                        <ReceiptDownloadButton transaction={tx} userEmail={user?.email} copy={t.receipt} locale={language} />
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Method Tab */}
          <TabsContent value="payment" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">{t.paymentMethod}</CardTitle>
                <CardDescription>{t.updateBillingInfo}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <PayPalUserPaymentOption copy={paypalCopy} />

                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold mb-1">{t.provider}</p>
                    <p className="text-sm capitalize text-muted-foreground">
                      {subscription.payment_provider || t.paypalAvailable}
                    </p>
                  </div>

                  {subscription.stripe_subscription_id && (
                    <div>
                      <p className="text-sm font-semibold mb-1">{t.subscriptionId}</p>
                      <p className="text-xs text-muted-foreground font-mono">
                        {subscription.stripe_subscription_id}
                      </p>
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Button className="w-full" variant="outline">
                    {t.updatePaymentMethod}
                  </Button>
                  <Button className="w-full" variant="outline">
                    {t.viewInvoices}
                  </Button>
                </div>

                {subscription.status === "active" && subscription.tier !== "basic" && (
                  <div className="border-t border-border pt-4">
                    <p className="text-sm text-muted-foreground mb-3">
                      {t.cancelPrompt}
                    </p>
                    <Button variant="outline" className="w-full text-destructive">
                      {t.cancelSubscription}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* FAQ */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">{t.faqTitle}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm font-semibold mb-1">{t.faqPlanQ}</p>
              <p className="text-xs text-muted-foreground">
                {t.faqPlanA}
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold mb-1">{t.faqRefundQ}</p>
              <p className="text-xs text-muted-foreground">
                {t.faqRefundA}
              </p>
            </div>
            <div>
              <p className="text-sm font-semibold mb-1">{t.faqFailQ}</p>
              <p className="text-xs text-muted-foreground">
                {t.faqFailA}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}